import { ChevronRight } from "lucide-react";

const FAQS = [
    {
        id: "compare-to-others",
        q: "How does Edara compare to other compliance and safety platforms we've evaluated?",
        a: [
            "Mature platforms in this category were built ten to twenty-five years ago as document and workflow stores. Most have added AI features more recently. They do what they do well, and the contractors who use them aren't wrong to.",
            "Edara is built differently. Three things separate us:",
            "AI-native intake. Edara was designed from day one to classify, extract, and flag credentials at upload — not as a feature layered on a legacy document store.",
            "An intelligence layer, not just a data layer. SENTINEL turns the platform's operational data into real-time leading and lagging indicators, contractor scorecards, and critical risk control verification at project, region, and group level. Most platforms capture data. Edara reads it first and flags what matters — the principal still decides, but with verified data, not raw uploads.",
            "Compliance, end-to-end. Workforce, plant, subcontractors, permits, incidents, audits, and intelligence on a single schema — not split across two or three compliance vendors.",
            "Edara isn't right for every contractor — depth of deployment history matters too. Edara is the choice for Tier 1 contractors who want the next generation of compliance: built AI-native, built in Australia, built for how Tier 1 projects actually run.",
        ],
    },
    {
        id: "deployment-time",
        q: "How quickly can we deploy on a live project?",
        a: [
            "Weeks, not quarters. Pre-built project templates for civil, energy, BESS, tunnelling, and building accelerate configuration. Subcontractor onboarding runs in parallel via the worker portal.",
        ],
    },
    {
        id: "subcontractor-licensing",
        q: "How are subcontractors licensed on the platform?",
        a: [
            "Subcontractor admins and their staff access Edara through a username-based portal. Subcontractor seats are licensed on a per-seat basis under the same model as the Tier 1 workforce — every worker on the platform carries a seat, and pricing is straightforward and consistent across the project.",
        ],
    },
    {
        id: "ai-intake",
        q: "How does the AI credential intake actually work?",
        a: [
            "When a worker uploads a credential, Edara classifies the document type, extracts holder name, document number, scope, and expiry, and flags wrong-document-type uploads or lapsed credentials before they reach the compliance officer. The compliance officer approves verified data rather than reading scans and retyping.",
        ],
    },
    {
        id: "australian-hosting",
        q: "Is data hosted in Australia?",
        a: ["Yes. All customer data is hosted on enterprise cloud infrastructure in Australia."],
    },
    {
        id: "enterprise-integration",
        q: "What does enterprise integration look like with our existing project controls stack?",
        a: [
            "SSO is supported via SAML 2.0 and OIDC, with SCIM for user provisioning. Standard connectors are available for common project controls and ERP platforms used on Tier 1 projects; bespoke integrations can be scoped during implementation as part of the deployment plan.",
        ],
    },
    {
        id: "group-governance",
        q: "How does Edara support our group-level governance reporting?",
        a: [
            "SENTINEL rolls project-level data up to region and group level automatically. Contractor scorecards, leading and lagging indicators, and critical risk control verification are visible by project, by package, by region, and at group executive level in real time.",
        ],
    },
    {
        id: "worker-no-corporate-email",
        q: "Can workers without a corporate email address access the platform?",
        a: [
            "Yes. Workers access Edara through username-based credentials issued at onboarding. A corporate email address is not required.",
        ],
    },
];

export default function FAQAccordion() {
    return (
        <div className="divide-y divide-zinc-200 border-y border-zinc-200">
            {FAQS.map((item, i) => (
                <details
                    key={item.id}
                    className="faq-item group"
                    data-testid={`faq-item-${i}`}
                    open={i === 0}
                >
                    <summary className="flex items-center justify-between gap-6 py-5 sm:py-6 px-1 sm:px-2">
                        <span className="font-sora font-semibold text-[#0A0A0A] text-base sm:text-lg leading-snug">
                            {item.q}
                        </span>
                        <ChevronRight className="faq-chevron w-5 h-5 text-zinc-500 shrink-0 rotate-90" />
                    </summary>
                    <div className="pb-6 px-1 sm:px-2 space-y-3 text-zinc-700 text-[15px] leading-relaxed max-w-3xl">
                        {item.a.map((paragraph) => (
                            <p key={`${item.id}-${paragraph.slice(0, 32)}`}>
                                {paragraph}
                            </p>
                        ))}
                    </div>
                </details>
            ))}
        </div>
    );
}
